import React from "react";
import { Sparkles, Clock, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { ExhibitionEvent } from "@/hooks/useExhibitionEvents";

interface ExhibitionEventBannerProps {
  event: ExhibitionEvent;
  countdown: { days: number; hours: number; minutes: number; seconds: number } | null;
  participantCount?: number;
  boothCount?: number;
  onEnter: () => void;
}

const ExhibitionEventBanner: React.FC<ExhibitionEventBannerProps> = ({
  event,
  countdown,
  participantCount = 0,
  boothCount = 0,
  onEnter,
}) => {
  const isLive = event.is_live;

  return (
    <div
      className={cn(
        "relative mx-4 mt-4 rounded-2xl overflow-hidden",
        isLive
          ? "bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500"
          : "bg-gradient-to-br from-pink-500 via-rose-500 to-red-500"
      )}
    >
      {/* Animated background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-32 h-32 bg-white/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse delay-300" />
      </div>

      <div className="relative p-5">
        {/* Status badge */}
        <div className="flex items-center gap-2 mb-3">
          {isLive ? (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
              </span>
              <span className="text-sm font-bold text-white">LIVE NOW</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
              <Clock className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">Coming Soon</span>
            </div>
          )}
          <Sparkles className="w-5 h-5 text-white/80 ml-auto" />
        </div>

        {/* Event name */}
        <h2 className="text-2xl font-bold text-white mb-1">
          {event.name}
        </h2>
        
        {event.description && (
          <p className="text-white/80 text-sm mb-4 line-clamp-2">
            {event.description}
          </p>
        )}

        {/* Countdown or Live Stats */}
        {isLive ? (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2 text-white/90">
              <Users className="w-4 h-4" />
              <span className="font-semibold">{participantCount}</span>
              <span className="text-white/70 text-sm">people live</span>
            </div>
            <div className="w-px h-4 bg-white/30" />
            <div className="text-white/90">
              <span className="font-semibold">{boothCount}</span>
              <span className="text-white/70 text-sm ml-1">booths</span>
            </div>
          </div>
        ) : countdown ? (
          <div className="flex items-center gap-2 mb-4">
            {countdown.days > 0 && (
              <TimeUnit value={countdown.days} label="d" />
            )}
            <TimeUnit value={countdown.hours} label="h" />
            <TimeUnit value={countdown.minutes} label="m" />
            <TimeUnit value={countdown.seconds} label="s" />
          </div>
        ) : null}

        {/* CTA Button */}
        <button
          onClick={onEnter}
          className={cn(
            "w-full py-3 rounded-xl font-bold text-lg transition-all shadow-lg",
            isLive
              ? "bg-white text-green-600 hover:bg-white/90"
              : "bg-white/20 text-white border border-white/30 hover:bg-white/30"
          )}
        >
          {isLive ? "Enter Exhibition" : "Set Reminder"}
        </button>
      </div>
    </div>
  );
};

const TimeUnit = ({ value, label }: { value: number; label: string }) => (
  <div className="flex items-baseline gap-0.5 px-2.5 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
    <span className="text-xl font-bold text-white">
      {value.toString().padStart(2, "0")}
    </span>
    <span className="text-xs text-white/70 uppercase">{label}</span>
  </div>
);

export default ExhibitionEventBanner;
